import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, Download, Search, FileText, RefreshCw, Eye } from 'lucide-react';
import { Seo } from '@/components/common/Seo';
import { OphirButton } from '@/components/common/OphirButton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  listSubmissions, getSubmission, updateSubmission, getStats, exportUrl,
  downloadDoc, getContent, updateContent, adminMe, changePassword,
} from '@/lib/api';

const STATUSES = [
  'New', 'Under Review', 'Information Requested', 'Qualified', 'Account Application',
  'Approved', 'Purchase Discussion', 'Not a Current Fit', 'Archived',
];
const STATUS_COLOR = {
  New: 'bg-[#315C91]/10 text-[#315C91]',
  'Under Review': 'bg-ophir-gold/15 text-ophir-gold-deep',
  'Information Requested': 'bg-ophir-gold/15 text-ophir-gold-deep',
  Qualified: 'bg-[#2F6B57]/12 text-[#2F6B57]',
  'Account Application': 'bg-ophir-navy/10 text-ophir-navy',
  Approved: 'bg-[#2F6B57]/15 text-[#2F6B57]',
  'Purchase Discussion': 'bg-ophir-navy/10 text-ophir-navy',
  'Not a Current Fit': 'bg-[#B42318]/10 text-[#B42318]',
  Archived: 'bg-ophir-grey text-ophir-muted',
};

function fmt(d) { try { return new Date(d).toLocaleString(); } catch { return d; } }
function fmtBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('submissions');

  // submissions
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');

  // content
  const [content, setContent] = useState(null);
  const [savingContent, setSavingContent] = useState(false);

  // account (change password)
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  const logout = () => { localStorage.removeItem('ophir_admin_token'); nav('/admin'); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (q) params.q = q;
      const [data, st] = await Promise.all([listSubmissions(params), getStats()]);
      setRows(data); setStats(st);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); return; }
      toast.error('Failed to load submissions');
    } finally { setLoading(false); }
  }, [typeFilter, statusFilter, q]);

  useEffect(() => {
    adminMe().then(setMe).catch(() => nav('/admin'));
    getContent().then(setContent).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try { const d = await getSubmission(id); setSelected(d); setNote(''); }
    catch { toast.error('Failed to open submission'); }
  };

  const changeStatus = async (status) => {
    try { const upd = await updateSubmission(selected.id, { status }); setSelected(upd); toast.success('Status updated'); load(); }
    catch { toast.error('Failed to update status'); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { const upd = await updateSubmission(selected.id, { add_note: note }); setSelected(upd); setNote(''); toast.success('Note added'); }
    catch { toast.error('Failed to add note'); }
  };

  const doDownload = async (doc) => {
    try {
      const blob = await downloadDoc(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = doc.original_name || 'document'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download document'); }
  };

  const doView = async (doc) => {
    try {
      const blob = await downloadDoc(doc.id);
      // Ensure the blob carries the correct MIME type so the browser renders it inline
      const typed = doc.content_type ? new Blob([blob], { type: doc.content_type }) : blob;
      const url = URL.createObjectURL(typed);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Failed to open document'); }
  };

  const doExport = async (type) => {
    try {
      const token = localStorage.getItem('ophir_admin_token');
      const res = await fetch(exportUrl(type === 'all' ? undefined : type), { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `ophir-submissions-${type}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const saveContent = async () => {
    setSavingContent(true);
    try { await updateContent(content); toast.success('Content saved'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
    finally { setSavingContent(false); }
  };

  const submitPassword = async () => {
    if (pw.new_password.length < 8) { toast.error('New password must be at least 8 characters.'); return; }
    if (pw.new_password !== pw.confirm) { toast.error('New passwords do not match.'); return; }
    setPwSaving(true);
    try {
      await changePassword(pw.current_password, pw.new_password);
      toast.success('Password updated. Please sign in again.');
      setTimeout(logout, 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update password');
    } finally { setPwSaving(false); }
  };
  const setC = (path, value) => {
    setContent((c) => {
      const next = structuredClone(c);
      let obj = next; const keys = path.split('.');
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const summary = (s) => {
    const d = s.data || {};
    if (s.type === 'supplier') return `${d.company_name || '\u2014'} \u00b7 ${d.contact_name || ''}`;
    if (s.type === 'buyer') return `${d.company_name || '\u2014'} \u00b7 ${d.contact_name || ''}`;
    if (s.type === 'rfq') return `${d.company_name || '\u2014'} \u00b7 ${d.full_name || ''}${d.product_category ? ` \u00b7 ${d.product_category}` : ''}`;
    return `${d.name || '\u2014'} \u00b7 ${d.inquiry_type || ''}`;
  };

  return (
    <>
      <Seo title="Dashboard | OPHIR SUPPLY Admin" description="Admin dashboard" />
      <div className="min-h-screen bg-ophir-ivory-soft">
        <header className="bg-white border-b border-ophir-grey sticky top-0 z-30">
          <div className="container-ophir flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <img src="/images/ophir/logo/ophir-logo-full.png" alt="OPHIR SUPPLY" className="h-8 w-auto" style={{ maxWidth: 180 }} />
              <span className="text-[13px] text-ophir-muted hidden sm:inline">Admin</span>
            </div>
            <div className="flex items-center gap-4">
              {me && <span className="text-[13px] text-ophir-muted hidden sm:inline">{me.email}</span>}
              <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-[14px] text-ophir-navy hover:text-ophir-navy-deep"><LogOut size={16} /> Sign out</button>
            </div>
          </div>
        </header>

        <div className="container-ophir py-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="submissions" data-testid="admin-tab-submissions">Submissions</TabsTrigger>
              <TabsTrigger value="content" data-testid="admin-tab-content">Site content</TabsTrigger>
              <TabsTrigger value="account" data-testid="admin-tab-account">Account</TabsTrigger>
            </TabsList>

            <TabsContent value="submissions">
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <StatCard label="Total submissions" value={stats.total} />
                  <StatCard label="New" value={stats.new} />
                  <StatCard label="Supplier" value={stats.by_type?.supplier || 0} />
                  <StatCard label="Buyer" value={stats.by_type?.buyer || 0} />
                </div>
              )}

              <div className="bg-white border border-ophir-grey rounded-2xl shadow-ophir">
                <div className="p-4 border-b border-ophir-grey flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
                  <div className="flex flex-wrap gap-3">
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger data-testid="admin-filter-type" className="w-[150px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All types</SelectItem>
                        <SelectItem value="supplier">Supplier</SelectItem>
                        <SelectItem value="buyer">Buyer</SelectItem>
                        <SelectItem value="rfq">Bulk Quote (RFQ)</SelectItem>
                        <SelectItem value="contact">Contact</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger data-testid="admin-filter-status" className="w-[180px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ophir-grey-med" />
                      <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search\u2026" className="pl-9 w-[220px]" />
                    </div>
                    <button onClick={load} className="inline-flex items-center gap-2 text-[14px] text-ophir-navy"><RefreshCw size={15} /> Refresh</button>
                  </div>
                  <div className="flex gap-2">
                    <OphirButton variant="secondary" data-testid="admin-export" onClick={() => doExport(typeFilter)} className="px-4 py-2 text-[13px]"><Download size={16} /> Export CSV</OphirButton>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left" data-testid="admin-submissions-table">
                    <thead>
                      <tr className="text-[12px] uppercase tracking-wider text-ophir-muted border-b border-ophir-grey">
                        <th className="px-4 py-3 font-semibold">Type</th>
                        <th className="px-4 py-3 font-semibold">Summary</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3 font-semibold">Received</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={4} className="px-4 py-10 text-center text-ophir-muted">Loading\u2026</td></tr>
                      ) : rows.length === 0 ? (
                        <tr><td colSpan={4} className="px-4 py-10 text-center text-ophir-muted">No submissions found.</td></tr>
                      ) : rows.map((s) => (
                        <tr key={s.id} data-testid="admin-submission-row" onClick={() => openDetail(s.id)} className="border-b border-ophir-grey/60 hover:bg-ophir-ivory-soft cursor-pointer">
                          <td className="px-4 py-3 text-[14px] capitalize text-ophir-navy">{s.type}</td>
                          <td className="px-4 py-3 text-[14px] text-ophir-text">{summary(s)}</td>
                          <td className="px-4 py-3"><span className={`inline-block rounded-full px-3 py-1 text-[12px] font-semibold ${STATUS_COLOR[s.status] || 'bg-ophir-grey'}`}>{s.status}</span></td>
                          <td className="px-4 py-3 text-[13px] text-ophir-muted whitespace-nowrap">{fmt(s.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="content">
              {content && (
                <div className="max-w-3xl space-y-6 pb-24">
                  <Panel title="Announcement bar">
                    <Field label="Text"><Input value={content.announcement?.text || ''} onChange={(e) => setC('announcement.text', e.target.value)} /></Field>
                  </Panel>
                  <Panel title="Hero">
                    <Field label="Headline line 1"><Input value={content.hero?.headline_line1 || ''} onChange={(e) => setC('hero.headline_line1', e.target.value)} /></Field>
                    <Field label="Headline line 2"><Input value={content.hero?.headline_line2 || ''} onChange={(e) => setC('hero.headline_line2', e.target.value)} /></Field>
                    <Field label="Body"><Textarea rows={3} value={content.hero?.body || ''} onChange={(e) => setC('hero.body', e.target.value)} /></Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Primary CTA label"><Input value={content.hero?.primary_cta?.label || ''} onChange={(e) => setC('hero.primary_cta.label', e.target.value)} /></Field>
                      <Field label="Secondary CTA label"><Input value={content.hero?.secondary_cta?.label || ''} onChange={(e) => setC('hero.secondary_cta.label', e.target.value)} /></Field>
                    </div>
                  </Panel>
                  <Panel title="Introduction">
                    <Field label="Label"><Input value={content.intro?.label || ''} onChange={(e) => setC('intro.label', e.target.value)} /></Field>
                    <Field label="Heading"><Input value={content.intro?.heading || ''} onChange={(e) => setC('intro.heading', e.target.value)} /></Field>
                    <Field label="Body"><Textarea rows={3} value={content.intro?.body || ''} onChange={(e) => setC('intro.body', e.target.value)} /></Field>
                    <Field label="Pull quote"><Input value={content.intro?.pullquote || ''} onChange={(e) => setC('intro.pullquote', e.target.value)} /></Field>
                  </Panel>
                  <Panel title="Trust strip">
                    <ListEditor items={content.trust || []} onChange={(v) => setContent((c) => ({ ...c, trust: v }))}
                      template={{ label: '', icon: 'package' }}
                      fields={[{ key: 'label', label: 'Label' }, { key: 'icon', label: 'Icon name' }]} addLabel="Add trust item" />
                  </Panel>
                  <Panel title="Core capabilities">
                    <ListEditor items={content.capabilities || []} onChange={(v) => setContent((c) => ({ ...c, capabilities: v }))}
                      template={{ title: '', body: '', icon: 'package' }}
                      fields={[{ key: 'title', label: 'Title' }, { key: 'icon', label: 'Icon name' }, { key: 'body', label: 'Body', type: 'textarea' }]} addLabel="Add capability" />
                  </Panel>
                  <Panel title="Product categories">
                    <ListEditor items={content.categories || []} onChange={(v) => setContent((c) => ({ ...c, categories: v }))}
                      template={{ slug: '', title: '', blurb: '', image: '' }}
                      fields={[{ key: 'title', label: 'Title' }, { key: 'blurb', label: 'Blurb', type: 'textarea' }, { key: 'image', label: 'Image path' }]} addLabel="Add category" />
                  </Panel>
                  <Panel title="Our process">
                    <ListEditor items={content.process || []} onChange={(v) => setContent((c) => ({ ...c, process: v }))}
                      template={{ n: 0, title: '', body: '' }}
                      fields={[{ key: 'title', label: 'Title' }, { key: 'body', label: 'Body', type: 'textarea' }]} addLabel="Add step" />
                  </Panel>
                  <Panel title="FAQs">
                    <ListEditor items={content.faqs || []} onChange={(v) => setContent((c) => ({ ...c, faqs: v }))}
                      template={{ q: '', a: '' }}
                      fields={[{ key: 'q', label: 'Question' }, { key: 'a', label: 'Answer', type: 'textarea' }]} addLabel="Add FAQ" />
                  </Panel>
                  <Panel title="Responsible brand representation">
                    <Field label="Heading"><Input value={content.responsible?.heading || ''} onChange={(e) => setC('responsible.heading', e.target.value)} /></Field>
                    <Field label="Body"><Textarea rows={3} value={content.responsible?.body || ''} onChange={(e) => setC('responsible.body', e.target.value)} /></Field>
                    <Field label="Disclaimer"><Textarea rows={2} value={content.responsible?.disclaimer || ''} onChange={(e) => setC('responsible.disclaimer', e.target.value)} /></Field>
                  </Panel>
                  <Panel title="Locations">
                    <ListEditor items={content.locations || []} onChange={(v) => setContent((c) => ({ ...c, locations: v }))}
                      template={{ name: '', address: '', map_query: '', status: '' }}
                      fields={[{ key: 'name', label: 'Office name' }, { key: 'address', label: 'Address', type: 'textarea' }, { key: 'map_query', label: 'Map search query' }, { key: 'status', label: 'Status badge (optional)' }]} addLabel="Add location" />
                  </Panel>
                  <Panel title="Contact details (optional)">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Email"><Input value={content.contact?.email || ''} onChange={(e) => setC('contact.email', e.target.value)} /></Field>
                      <Field label="Phone"><Input value={content.contact?.phone || ''} onChange={(e) => setC('contact.phone', e.target.value)} /></Field>
                    </div>
                  </Panel>
                  <Panel title="Footer">
                    <Field label="Description"><Textarea rows={3} value={content.footer?.description || ''} onChange={(e) => setC('footer.description', e.target.value)} /></Field>
                    <Field label="Brand line"><Input value={content.footer?.brand_line || ''} onChange={(e) => setC('footer.brand_line', e.target.value)} /></Field>
                  </Panel>
                  <Panel title="SEO (per page)">
                    {['home', 'about', 'capabilities', 'categories', 'suppliers', 'buyers', 'contact'].map((pg) => (
                      <div key={pg} className="border-t border-ophir-grey pt-4 first:border-0 first:pt-0">
                        <p className="text-[12px] font-semibold uppercase tracking-wide text-ophir-gold-deep mb-2">{pg}</p>
                        <Field label="Title"><Input value={content.seo?.[pg]?.title || ''} onChange={(e) => setC(`seo.${pg}.title`, e.target.value)} /></Field>
                        <Field label="Meta description"><Textarea rows={2} value={content.seo?.[pg]?.description || ''} onChange={(e) => setC(`seo.${pg}.description`, e.target.value)} /></Field>
                      </div>
                    ))}
                  </Panel>
                  <div className="sticky bottom-4">
                    <OphirButton data-testid="admin-save-content" onClick={saveContent} disabled={savingContent}>{savingContent ? 'Saving\u2026' : 'Save all content'}</OphirButton>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="account">
              <div className="max-w-md">
                <Panel title="Change password">
                  <p className="text-[13.5px] text-ophir-muted -mt-1 mb-2">Signed in as {me?.email}. Update your administrator password below.</p>
                  <Field label="Current password">
                    <Input type="password" data-testid="admin-current-password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} />
                  </Field>
                  <Field label="New password (min 8 characters)">
                    <Input type="password" data-testid="admin-new-password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} />
                  </Field>
                  <Field label="Confirm new password">
                    <Input type="password" data-testid="admin-confirm-password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} />
                  </Field>
                  <OphirButton data-testid="admin-change-password-submit" onClick={submitPassword} disabled={pwSaving}>
                    {pwSaving ? 'Updating\u2026' : 'Update password'}
                  </OphirButton>
                </Panel>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif capitalize">{selected.type} submission</DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <span className="text-[13px] text-ophir-muted">Status</span>
                  <Select value={selected.status} onValueChange={changeStatus}>
                    <SelectTrigger data-testid="admin-detail-status" className="w-[220px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div className="rounded-xl border border-ophir-grey bg-ophir-ivory-soft p-4">
                  <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[14px]">
                    {Object.entries(selected.data || {}).filter(([k]) => !['consent', 'documents_meta', 'document_ids'].includes(k)).map(([k, v]) => (
                      <div key={k} className="min-w-0">
                        <dt className="text-[11px] uppercase tracking-wide text-ophir-muted">{k.replace(/_/g, ' ')}</dt>
                        <dd className="text-ophir-navy break-words">{Array.isArray(v) ? (v.join(', ') || '\u2014') : (String(v) || '\u2014')}</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                {selected.documents && selected.documents.length > 0 && (
                  <div>
                    <h4 className="text-[13px] font-semibold uppercase tracking-wide text-ophir-muted mb-2">Documents ({selected.documents.length})</h4>
                    <ul className="space-y-2">
                      {selected.documents.map((d) => (
                        <li key={d.id} data-testid="admin-document-row" className="flex items-center justify-between gap-3 rounded-lg border border-ophir-grey px-3 py-2">
                          <span className="flex items-center gap-2 text-[14px] text-ophir-navy min-w-0">
                            <FileText size={16} className="text-ophir-gold-deep shrink-0" />
                            <span className="min-w-0">
                              <span className="block truncate">{d.original_name}</span>
                              <span className="block text-[11.5px] text-ophir-muted">{fmtBytes(d.size)}{d.content_type ? ` \u00b7 ${d.content_type}` : ''}</span>
                            </span>
                          </span>
                          <span className="flex items-center gap-1 shrink-0">
                            <button data-testid="admin-document-view" onClick={() => doView(d)} title="View" className="p-1.5 rounded-md text-ophir-navy hover:bg-ophir-ivory-soft hover:text-ophir-navy-deep"><Eye size={16} /></button>
                            <button data-testid="admin-document-download" onClick={() => doDownload(d)} title="Download" className="p-1.5 rounded-md text-ophir-navy hover:bg-ophir-ivory-soft hover:text-ophir-navy-deep"><Download size={16} /></button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <h4 className="text-[13px] font-semibold uppercase tracking-wide text-ophir-muted mb-2">Internal notes</h4>
                  {(selected.notes || []).length > 0 ? (
                    <ul className="space-y-2 mb-3">
                      {selected.notes.map((n, i) => (
                        <li key={i} className="rounded-lg bg-ophir-ivory-soft border border-ophir-grey px-3 py-2 text-[14px]">
                          <p className="text-ophir-navy">{n.text}</p>
                          <p className="mt-1 text-[12px] text-ophir-muted">{n.author} \u00b7 {fmt(n.at)}</p>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-[13px] text-ophir-muted mb-3">No notes yet.</p>}
                  <div className="flex gap-2">
                    <Textarea data-testid="admin-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note\u2026" />
                    <OphirButton data-testid="admin-note-add" onClick={addNote} className="shrink-0 self-end">Add</OphirButton>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const StatCard = ({ label, value }) => (
  <div className="bg-white border border-ophir-grey rounded-xl p-4 shadow-ophir">
    <p className="text-[12px] uppercase tracking-wide text-ophir-muted">{label}</p>
    <p className="mt-1 font-serif text-[28px] text-ophir-navy-deep">{value}</p>
  </div>
);

const Panel = ({ title, children }) => (
  <div className="bg-white border border-ophir-grey rounded-2xl shadow-ophir p-6 space-y-4">
    <h3 className="font-serif text-[18px] text-ophir-navy-deep">{title}</h3>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[12px] font-semibold uppercase tracking-[0.1em] text-ophir-navy">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);

const ListEditor = ({ items = [], onChange, fields, template, addLabel = 'Add item' }) => {
  const update = (idx, key, value) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { ...template }]);
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  return (
    <div className="space-y-4">
      {items.map((item, idx) => (
        <div key={idx} className="rounded-xl border border-ophir-grey bg-ophir-ivory-soft p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] font-semibold text-ophir-muted">#{idx + 1}</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => move(idx, -1)} className="text-[13px] text-ophir-navy disabled:opacity-30" disabled={idx === 0}>↑</button>
              <button type="button" onClick={() => move(idx, 1)} className="text-[13px] text-ophir-navy disabled:opacity-30" disabled={idx === items.length - 1}>↓</button>
              <button type="button" onClick={() => remove(idx)} className="text-[13px] text-[#B42318]">Remove</button>
            </div>
          </div>
          <div className="space-y-3">
            {fields.map((f) => (
              <Field key={f.key} label={f.label}>
                {f.type === 'textarea'
                  ? <Textarea rows={2} value={item[f.key] || ''} onChange={(e) => update(idx, f.key, e.target.value)} />
                  : <Input value={item[f.key] || ''} onChange={(e) => update(idx, f.key, e.target.value)} />}
              </Field>
            ))}
          </div>
        </div>
      ))}
      <OphirButton variant="secondary" onClick={add} className="px-4 py-2 text-[13px]">{addLabel}</OphirButton>
    </div>
  );
};
